import { getMediaBaseUrl } from "../config/clientConfig";

/**
 * Maps assessment API parts onto the CarDamage3D viewer's part-key contract.
 *
 * The viewer (src/components/CarDamage3D.jsx) addresses panels by short keys —
 * front_bumper, bonnet, fender_r, door_fr, lamp_r, mirror_l, … — while the
 * assessment API returns human part names ("right front door") plus an
 * `Intensity` 0–10 score and an `Action` recommendation. Everything that
 * translates between those two worlds lives here, so swapping the schematic
 * model for real per-body-type meshes later only needs the key contract kept.
 */

/* ── Severity ─────────────────────────────────────────────────────────── */

// Score bands, matching the Clearline mock (8.4 severe · 4.6 moderate · 1.4 minor).
export const SEVERE_THRESHOLD = 7;
export const MODERATE_THRESHOLD = 3;

// `color` is kept in sync by hand with CarDamage3D.jsx's own SEV_COLORS (the
// 3D panel fill) — both need to agree or the model and its own legend show
// two different reds for "severe".
export const SEVERITY_META = {
  severe: {
    label: "Severe",
    color: "#eb8a8a",
    chipBg: "#fbe9e9",
    chipFg: "#c22f2f",
    rank: 3,
  },
  moderate: {
    label: "Moderate",
    color: "#f4b685",
    chipBg: "#fdf0dd",
    chipFg: "#c26a1e",
    rank: 2,
  },
  minor: {
    label: "Minor",
    color: "#f6d68c",
    chipBg: "#fdf6df",
    chipFg: "#a37f12",
    rank: 1,
  },
};

export const NO_DAMAGE_COLOR = "#dfe3ea";

/** severity bucket for a 0–10 intensity score; null when undamaged. */
export const severityFromScore = (score) => {
  const value = Number(score);
  if (!Number.isFinite(value) || value <= 0) return null;
  if (value >= SEVERE_THRESHOLD) return "severe";
  if (value >= MODERATE_THRESHOLD) return "moderate";
  return "minor";
};

/** Accepts either a bucket name from the API or a numeric score. */
export const normalizeSeverity = (value) => {
  if (typeof value === "string") {
    const key = value.trim().toLowerCase();
    if (SEVERITY_META[key]) return key;
    if (key.includes("severe") || key.includes("high")) return "severe";
    if (key.includes("moderate") || key.includes("medium")) return "moderate";
    if (key.includes("minor") || key.includes("low")) return "minor";
    return null;
  }
  return severityFromScore(value);
};

/* ── Part keys ────────────────────────────────────────────────────────── */

/** Display label per viewer key — kept in sync with CarDamage3D's LABELS. */
export const PART_LABELS = {
  shell: "Body shell",
  front_bumper: "Front bumper",
  rear_bumper: "Rear bumper",
  bonnet: "Bonnet",
  roof: "Roof",
  boot: "Boot lid",
  grille: "Front grille",
  fender_l: "Left fender",
  fender_r: "Right fender",
  door_fl: "Left front door",
  door_fr: "Right front door",
  door_rl: "Left rear door",
  door_rr: "Right rear door",
  quarter_l: "Left quarter panel",
  quarter_r: "Right quarter panel",
  lamp_l: "Left head lamp",
  lamp_r: "Right head lamp",
  tail_l: "Left tail lamp",
  tail_r: "Right tail lamp",
  fog_l: "Left fog lamp",
  fog_r: "Right fog lamp",
  mirror_l: "Left mirror",
  mirror_r: "Right mirror",
  skirt_l: "Left running board",
  skirt_r: "Right running board",
  windshield: "Front windshield",
  rear_windshield: "Rear windshield",
  window_fl: "Left front window",
  window_fr: "Right front window",
  window_rl: "Left rear window",
  window_rr: "Right rear window",
  quarter_glass_l: "Left quarter glass",
  quarter_glass_r: "Right quarter glass",
  pillar_a_l: "Left A-pillar",
  pillar_a_r: "Right A-pillar",
  pillar_b_l: "Left B-pillar",
  pillar_b_r: "Right B-pillar",
  pillar_c_l: "Left C-pillar",
  pillar_c_r: "Right C-pillar",
  wheel_fl: "Left front rim",
  wheel_fr: "Right front rim",
  wheel_rl: "Left rear rim",
  wheel_rr: "Right rear rim",
  fuel_cap: "Fuel cap",
};

/** Where the panel sits on the car — shown under the name in the detail card. */
export const PART_LOCATIONS = {
  shell: "Whole body",
  front_bumper: "Front",
  rear_bumper: "Rear",
  bonnet: "Front",
  roof: "Top",
  boot: "Rear",
  grille: "Front",
  fender_l: "Left front",
  fender_r: "Right front",
  door_fl: "Left front",
  door_fr: "Right front",
  door_rl: "Left rear",
  door_rr: "Right rear",
  quarter_l: "Left rear",
  quarter_r: "Right rear",
  lamp_l: "Left front",
  lamp_r: "Right front",
  tail_l: "Left rear",
  tail_r: "Right rear",
  fog_l: "Left front",
  fog_r: "Right front",
  mirror_l: "Left",
  mirror_r: "Right",
  skirt_l: "Left",
  skirt_r: "Right",
  windshield: "Front",
  rear_windshield: "Rear",
  window_fl: "Left front",
  window_fr: "Right front",
  window_rl: "Left rear",
  window_rr: "Right rear",
  quarter_glass_l: "Left rear",
  quarter_glass_r: "Right rear",
  pillar_a_l: "Left front",
  pillar_a_r: "Right front",
  pillar_b_l: "Left",
  pillar_b_r: "Right",
  pillar_c_l: "Left rear",
  pillar_c_r: "Right rear",
  wheel_fl: "Left front",
  wheel_fr: "Right front",
  wheel_rl: "Left rear",
  wheel_rr: "Right rear",
  fuel_cap: "Right rear",
};

/**
 * Normalized API part name → viewer key.
 * Parts the schematic model has no mesh for (rims, glass, grill, trim …) are
 * deliberately absent: they still appear in the damaged-parts list, they just
 * can't be located in 3D.
 */
const API_NAME_TO_KEY = {
  "front bumper": "front_bumper",
  "rear bumper": "rear_bumper",
  bonnet: "bonnet",
  hood: "bonnet",
  boot: "boot",
  "boot lid": "boot",
  dickie: "boot",
  trunk: "boot",
  roof: "roof",
  "left fender": "fender_l",
  "right fender": "fender_r",
  "left front door": "door_fl",
  "right front door": "door_fr",
  "left rear door": "door_rl",
  "right rear door": "door_rr",
  "left quarter panel": "quarter_l",
  "right quarter panel": "quarter_r",
  "left head lamp": "lamp_l",
  "right head lamp": "lamp_r",
  "left headlamp": "lamp_l",
  "right headlamp": "lamp_r",
  "left tail lamp": "tail_l",
  "right tail lamp": "tail_r",
  "left taillamp": "tail_l",
  "right taillamp": "tail_r",
  "left mirror": "mirror_l",
  "right mirror": "mirror_r",
  "left orvm": "mirror_l",
  "right orvm": "mirror_r",
  // Panels the detailed sedan mesh adds over the old schematic model.
  grill: "grille",
  grille: "grille",
  "front grille": "grille",
  "left fog lamp": "fog_l",
  "right fog lamp": "fog_r",
  "left running board": "skirt_l",
  "right running board": "skirt_r",
  "left side skirt": "skirt_l",
  "right side skirt": "skirt_r",
  "front windshield": "windshield",
  windshield: "windshield",
  "rear windshield": "rear_windshield",
  "left front window": "window_fl",
  "right front window": "window_fr",
  "left rear window": "window_rl",
  "right rear window": "window_rr",
  "left quarter glass": "quarter_glass_l",
  "right quarter glass": "quarter_glass_r",
  "left apillar": "pillar_a_l",
  "right apillar": "pillar_a_r",
  "left a pillar": "pillar_a_l",
  "right a pillar": "pillar_a_r",
  "left bpillar": "pillar_b_l",
  "right bpillar": "pillar_b_r",
  "left cpillar": "pillar_c_l",
  "right cpillar": "pillar_c_r",
  "left front rim": "wheel_fl",
  "right front rim": "wheel_fr",
  "left rear rim": "wheel_rl",
  "right rear rim": "wheel_rr",
  cap: "fuel_cap",
  "fuel cap": "fuel_cap",
  "fuel lid": "fuel_cap",
};

/** Lowercase, collapse underscores/hyphens/whitespace. */
export const normalizePartName = (name) =>
  String(name || "")
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** API part name → viewer key, or null when the model has no such panel. */
export const partKeyFromApiName = (name) => {
  const normalized = normalizePartName(name);
  if (!normalized) return null;
  return API_NAME_TO_KEY[normalized] || null;
};

/** Title-cases an API part or side name for display. */
export const formatPartName = (name) => {
  const normalized = normalizePartName(name).replace(
    /([a-z])([A-Z])/g,
    "$1 $2",
  );
  if (!normalized) return "";
  return normalized.replace(/\b\w/g, (c) => c.toUpperCase());
};

/** "frontLeft" / "front_left" → "Front Left". */
export const formatSideName = (side) => {
  if (!side) return "";
  return formatPartName(String(side).replace(/([a-z])([A-Z])/g, "$1 $2"));
};

/* ── Assessment → viewer model ────────────────────────────────────────── */

// Resolved per call, not as a module constant: the media host is per-client and
// the client is only known after login. This file previously pinned Ergo's host,
// so every image 404'd for Bajaj and Hegic.
const toMediaUrl = (path) => {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = getMediaBaseUrl();
  return `${base}${path.startsWith("/") ? path : `/${path}`}`;
};

const pickImageUrl = (image, toUrl) => {
  if (!image) return "";
  if (typeof image === "string") return toUrl(image);
  return toUrl(
    image.picture || image.upload_picture || image.image || image.url || "",
  );
};

const readScore = (part) => {
  const candidates = [part?.Intensity, part?.intensity, part?.damage_intensity];
  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value)) return value;
  }
  return 0;
};

const readConfidence = (part) => {
  const candidates = [
    part?.confidence,
    part?.Confidence,
    part?.confidence_score,
    part?.accuracy,
  ];
  for (const candidate of candidates) {
    const value = Number(candidate);
    if (!Number.isFinite(value)) continue;
    // Accept both 0–1 and 0–100 encodings.
    const pct = value <= 1 ? value * 100 : value;
    return Math.round(Math.min(100, Math.max(0, pct)));
  }
  return null;
};

/**
 * Turns `assessmentData.assessment_details` into everything the Damage report
 * tab needs.
 *
 * @param {Object} assessmentData
 * @param {{locatableKeys?: Set<string>}} [options] keys the active 3D model
 *        covers; parts outside it are flagged `locatable: false` so the UI can
 *        say the panel isn't on the mesh.
 * @returns {{
 *   damageMap: Record<string, "severe"|"moderate"|"minor">,
 *   parts: Array<Object>,          // every part the API returned
 *   byKey: Record<string, Object>, // viewer key → part (worst wins on collision)
 *   damagedParts: Array<Object>,   // damaged only, worst first
 *   damagedCount: number,
 *   totalParts: number,
 *   worstKey: string|null,         // worst part the model can locate
 * }}
 */
/**
 * @param {{locatableKeys?: Set<string>, resolveMediaUrl?: (path: string) => string}} [options]
 *   `resolveMediaUrl` is the media resolver for the deployment the page is
 *   pointed at. Omitted, images resolve against the logged-in client — which
 *   is what every caller wanted until a second deployment reused the page.
 */
export const buildDamageModel = (
  assessmentData,
  { locatableKeys, resolveMediaUrl } = {},
) => {
  const toUrl = resolveMediaUrl || toMediaUrl;

  // Which keys the active 3D model can actually point at. Absent = assume all.
  const canLocate = (key) =>
    Boolean(key) && (!locatableKeys || locatableKeys.has(key));

  const details = Array.isArray(assessmentData?.assessment_details)
    ? assessmentData.assessment_details
    : [];

  const parts = details.map((part, index) => {
    const apiName = part?.part_name || "";
    const key = partKeyFromApiName(apiName);
    const score = readScore(part);
    const severity =
      normalizeSeverity(part?.damage_severity) ?? severityFromScore(score);
    const action = (
      part?.Action ||
      part?.action ||
      part?.suggested_action ||
      ""
    )
      .toString()
      .trim();

    // Wrapped rather than passed by reference: .map would hand pickImageUrl
    // the array index as its resolver.
    const damageImages = Array.isArray(part?.damage_images)
      ? part.damage_images.map((image) => pickImageUrl(image, toUrl)).filter(Boolean)
      : [];
    const partImages = Array.isArray(part?.part_images)
      ? part.part_images.map((image) => pickImageUrl(image, toUrl)).filter(Boolean)
      : [];
    const photos = Array.from(new Set([...damageImages, ...partImages]));

    return {
      id: part?.id ?? `${normalizePartName(apiName) || "part"}-${index}`,
      key,
      apiName,
      label: key ? PART_LABELS[key] : formatPartName(apiName),
      location: key
        ? PART_LOCATIONS[key]
        : formatSideName(part?.side) || "Not located on model",
      side: formatSideName(part?.side),
      score,
      severity: action.toLowerCase() === "no damage" ? null : severity,
      action,
      confidence: readConfidence(part),
      // Hegic reports on a 1-5 scale, the legacy contract on 0-10, so the scale
      // travels with the score instead of being assumed by whatever renders it.
      scoreMax: Number(part?.score_max) || 10,
      intensityLabel: part?.intensity_label || "",
      // Set when a human overruled the AI, so the view can say whose figure
      // this is rather than presenting an edit as the original assessment.
      editedBy: part?.edited_by || null,
      fromHistory: Boolean(part?.from_history),
      photos,
      photoCount: photos.length,
      locatable: canLocate(key),
      raw: part,
    };
  });

  /**
   * Viewer key → the panel, when the API reports one panel over several rows.
   *
   * The worse finding wins the severity, but the photographs are pooled: each
   * row is a different camera angle on the same panel, so discarding the loser
   * threw away evidence. That was why a panel photographed five times showed a
   * single image.
   */
  const mergePhotos = (a, b) => {
    const photos = Array.from(new Set([...a.photos, ...b.photos]));
    return { photos, photoCount: photos.length };
  };

  const byKey = {};
  parts.forEach((part) => {
    if (!part.key) return;
    const existing = byKey[part.key];
    if (!existing) {
      byKey[part.key] = part;
      return;
    }
    const worst = part.score > existing.score ? part : existing;
    byKey[part.key] = { ...worst, ...mergePhotos(existing, part) };
  });

  const damageMap = {};
  Object.entries(byKey).forEach(([key, part]) => {
    if (part.severity) damageMap[key] = part.severity;
  });

  // One row per panel in the list, for the same reason: two API rows for one
  // panel is one panel to review, with both sets of photographs.
  const damagedByPanel = new Map();
  parts
    .filter((part) => part.severity)
    .forEach((part) => {
      const id = part.key || part.apiName || part.id;
      const existing = damagedByPanel.get(id);
      if (!existing) {
        damagedByPanel.set(id, part);
        return;
      }
      const worst = part.score > existing.score ? part : existing;
      damagedByPanel.set(id, { ...worst, ...mergePhotos(existing, part) });
    });

  const damagedParts = [...damagedByPanel.values()].sort(
    (a, b) => b.score - a.score,
  );

  return {
    damageMap,
    parts,
    byKey,
    damagedParts,
    damagedCount: damagedParts.length,
    totalParts: parts.length,
    worstKey: damagedParts.find((part) => part.locatable)?.key || null,
  };
};
